import React, { useMemo, useState } from 'react';
import { addMonths, format } from 'date-fns';

interface Props {
  eq: any;
  access: any;
  showInvRefresh?: boolean;
  showPatRefresh?: boolean;
  onEdit: (eq: any) => void;
  onDelete?: (id: number) => void;
  onDuplicate?: (eq: any) => void;
  onRefreshInventory: (id: number) => void;
  onRefreshPat: (id: number) => void;
  onShowHistory?: (id: number) => void;
}

const EF = ({ label, value, cls }: { label: string; value?: string | null; cls?: string }) => (
  <div className="ef">
    <div className="eq-detail-lbl">{label}</div>
    <div className={`eq-detail-val${cls ? ' ' + cls : ''}`}>{value || '—'}</div>
  </div>
);

const EquipExpandPanel: React.FC<Props> = ({
  eq, access, showInvRefresh, showPatRefresh,
  onEdit, onDelete, onDuplicate, onRefreshInventory, onRefreshPat, onShowHistory,
}) => {
  const [confirming, setConfirming] = useState(false);
  const patPct = useMemo(() => {
    if (!eq.pat_date || !eq.pat_due) return null;
    const from = new Date(eq.pat_date).getTime();
    const to = addMonths(new Date(eq.pat_date), eq.pat_due).getTime();
    return Math.min(100, Math.round(((Date.now() - from) / (to - from)) * 100));
  }, [eq.pat_date, eq.pat_due]);

  const patColor = patPct === null ? '#9ca3af' : patPct >= 100 ? '#dc2626' : patPct > 75 ? '#d97706' : '#059669';
  const patDueDate = eq.pat_date && eq.pat_due ? addMonths(new Date(eq.pat_date), eq.pat_due) : null;

  const fmtDate = (d: string | Date | null | undefined) => {
    if (!d) return null;
    try { return format(new Date(d as any), 'dd MMM yyyy'); } catch { return null; }
  };

  return (
    <>
      {/* 4-column detail grid */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18,
        padding: '16px 20px',
        background: 'var(--btn-user-form)',
        borderTop: '2px solid rgba(37,99,235,.12)',
      }}>
        {/* Col 1: Asset */}
        <div>
          <div className="eq-eg-title">Asset</div>
          <EF label="Manufacturer" value={eq.manufacturer} />
          <EF label="Model" value={eq.model} />
          <EF label="Serial" value={eq.serial_number} cls="hi" />
          <EF label="Cost" value={eq.cost ? `£${eq.cost}` : null} />
        </div>

        {/* Col 2: PAT Test */}
        <div>
          <div className="eq-eg-title">PAT Test</div>
          {eq.pat_test ? (
            <>
              <EF label="Last tested" value={fmtDate(eq.pat_date)} />
              <EF
                label="Due"
                value={patDueDate ? format(patDueDate, 'MMM yyyy') : null}
                cls={patPct !== null && patPct >= 100 ? 'bad' : 'ok'}
              />
              <EF
                label="Tested by"
                value={eq.patUser ? `${eq.patUser.first_name} ${eq.patUser.last_name}` : null}
              />
              {patPct !== null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>Now</span>
                  <div className="eq-pat-tl-bar">
                    <div style={{
                      height: '100%', borderRadius: 2,
                      width: `${patPct}%`,
                      background: patColor,
                      transition: 'width .5s',
                    }} />
                  </div>
                  <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>Due</span>
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)' }}>Not required</div>
          )}
        </div>

        {/* Col 3: Inventory */}
        <div>
          <div className="eq-eg-title">Inventory</div>
          <EF label="Last inventory" value={fmtDate(eq.inventory_date)} />
          <EF
            label="By"
            value={eq.inventoryUser ? `${eq.inventoryUser.first_name} ${eq.inventoryUser.last_name}` : null}
          />
          <EF label="Purchase" value={fmtDate(eq.purchase_date)} />
          <EF
            label="Warranty"
            value={fmtDate(eq.warranty)}
            cls={eq.warranty && new Date(eq.warranty) < new Date() ? 'bad' : undefined}
          />
        </div>

        {/* Col 4: Capitalisation */}
        <div>
          <div className="eq-eg-title">Capitalisation</div>
          <EF label="Capitalised" value={eq.capitalised ? 'Yes' : 'No'} cls={eq.capitalised ? 'hi' : 'dim'} />
          {eq.capitalised && (
            <>
              <EF label="Method" value={eq.depreciation_method} />
              <EF label="Net book value" value={eq.net_book_value ? `£${eq.net_book_value}` : null} />
              <EF label="Funding strand" value={eq.funding_strand} />
            </>
          )}
          <EF label="Rental" value={eq.rental_opportunity ? 'Yes' : 'No'} cls={eq.rental_opportunity ? 'ok' : 'dim'} />
          {eq.notes && <EF label="Notes" value={eq.notes} />}
        </div>
      </div>

      {/* Action bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 20px 12px',
        background: 'var(--btn-user-form)',
        borderTop: '1px solid var(--border-input)',
      }}>
        {/* Left: name + quick refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text-color)' }}>
            🖥 {eq.item_name}
          </span>
          <span style={{ color: 'var(--text-color-calendar-week)' }}>· ID {eq.inventory_id} ·</span>
          {eq.status && (
            <span style={{ color: eq.status.color ?? '#9ca3af', fontWeight: 600 }}>{eq.status.name}</span>
          )}
          {onShowHistory && (
            <button
              type="button"
              className="eq-quick-btn"
              onClick={() => onShowHistory(eq.id)}
              title="Show change history"
            >📋 History</button>
          )}
          {showInvRefresh && (
            <button
              type="button"
              className="eq-quick-btn"
              onClick={() => onRefreshInventory(eq.id)}
              title="Set inventory date to today"
            >⟳ Inventory date</button>
          )}
          {showPatRefresh && eq.pat_test && (
            <button
              type="button"
              className="eq-quick-btn pat"
              onClick={() => onRefreshPat(eq.id)}
              title="Set PAT test date to today"
            >⟳ PAT test date</button>
          )}
        </div>

        {/* Right: action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {onDuplicate && access?.equipment_add && (
            <button
              type="button"
              onClick={() => onDuplicate(eq)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                border: '1px solid var(--border-input)', background: 'var(--btn-user-form)',
                color: 'var(--text-color)', cursor: 'pointer', transition: 'all .15s',
              }}
            >⧉ Duplicate</button>
          )}
          {onDelete && access?.equipment_delete && !confirming && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                border: '1px solid rgba(220,38,38,.2)', background: 'rgba(220,38,38,.07)',
                color: '#dc2626', cursor: 'pointer', transition: 'all .15s',
              }}
            >🗑 Archive</button>
          )}
          {onDelete && access?.equipment_delete && confirming && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Move to archive?</span>
              <button
                type="button"
                onClick={() => { onDelete(eq.id); setConfirming(false); }}
                style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer',
                }}
              >Yes</button>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: '1px solid var(--border-input)', background: 'var(--btn-user-form)',
                  color: 'var(--text-color)', cursor: 'pointer',
                }}
              >No</button>
            </div>
          )}
          {access?.equipment_edit && (
            <button
              type="button"
              onClick={() => onEdit(eq)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 16px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                border: 'none', background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                color: '#fff', cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(37,99,235,.25)', transition: 'all .15s',
              }}
            >✎ Edit</button>
          )}
        </div>
      </div>
    </>
  );
};

export default EquipExpandPanel;
